/**
 * Drag / add / remove the Vest position's TP and SL from a `.VEST` chart
 * (#639). Pure: no store, no chart, no socket.
 *
 * WRITE PATH. Exactly what the perps overlay sends, nothing new:
 * - move or create a leg: `broker:replace-bracket` with ONLY that leg's price
 *   (`tpPrice` or `slPrice`). tradovate-service's VestBrokerAdapter.replaceBracket
 *   moves the leg when one is working and creates it when not, and leaves the
 *   other leg alone (the overlay's +TP / +SL). Exit side, the position's size,
 *   the market's WIRE symbol (`BTC-PERP`), never the `.VEST` chart symbol.
 *   A created SL is a stop-market (`slExecution: 'market'`, as the overlay).
 * - remove a leg: `broker:cancel-order` with the leg's Vest id (the positions
 *   row carries it as `takeProfitOrderId` / `stopLossOrderId`); the adapter
 *   routes a TP / SL id to its own DELETE.
 * Both go to the row's broker, whose rows are its SELECTED account only (the
 * account the overlay trades), so no account id is sent.
 *
 * ENTRIES STAY OFF. Nothing here builds an entry, a flatten or a size change,
 * and a builder refuses (returns null) for a `.VEST` symbol or an index market.
 */

import { isVestFeedSymbol, roundToTick, tickDecimals } from './vestFeed';
import { vestChartSymbolFor, type VestPositionRow } from './vestPositionLines';

export type VestLegKind = 'tp' | 'sl';

export const VEST_REPLACE_BRACKET = 'broker:replace-bracket';
export const VEST_CANCEL_ORDER = 'broker:cancel-order';
export const VEST_BRACKET_REPLACED = 'broker:bracket-replaced';
export const VEST_ORDER_CANCELLED = 'broker:order-cancelled';
export const VEST_BROKER_ERROR = 'broker:error';

/** A TP / SL more than this fraction from the entry is refused (a price off another chart's scale). */
export const VEST_SANE_BAND = 0.5;

/** Default distance of a new (+TP / +SL) line from the reference price. */
export const VEST_DRAFT_FRACTION = 0.01;

export interface VestTpslCommand {
  event: typeof VEST_REPLACE_BRACKET | typeof VEST_CANCEL_ORDER;
  payload: Record<string, unknown>;
}

/** The key a leg's line is held under (same as vestPositionLines' price-line key). */
export function vestLegKey(row: Pick<VestPositionRow, 'brokerId' | 'vestSymbol'>, kind: VestLegKind): string {
  return `${row.brokerId}:${row.vestSymbol}:${kind}`;
}

/** The market's tick: the row's, else the chart's, else from the row's decimals; null when unknown. */
export function vestRowTick(row: Pick<VestPositionRow, 'tickSize' | 'priceDecimals'>, fallbackTick: number | null): number | null {
  if (row.tickSize !== null && row.tickSize > 0) return row.tickSize;
  if (fallbackTick !== null && fallbackTick > 0) return fallbackTick;
  if (row.priceDecimals !== null) return Number((10 ** -row.priceDecimals).toFixed(row.priceDecimals));
  return null;
}

export function vestRowDecimals(row: Pick<VestPositionRow, 'tickSize' | 'priceDecimals'>, fallbackTick: number | null): number {
  if (row.priceDecimals !== null) return row.priceDecimals;
  const t = vestRowTick(row, fallbackTick);
  return t === null ? 2 : tickDecimals(t);
}

/** A price snapped to the market's tick (unchanged when the tick is unknown). */
export function snapVestPrice(price: number, tick: number | null): number {
  return tick === null ? price : roundToTick(price, tick);
}

/** The chart's last trade as the mark, when it is plausibly this market's (within 50% of the entry). */
export function vestSaneMark(row: Pick<VestPositionRow, 'entryPrice'>, lastPrice: number | null | undefined): number | null {
  if (typeof lastPrice !== 'number' || !Number.isFinite(lastPrice) || lastPrice <= 0) return null;
  return Math.abs(lastPrice - row.entryPrice) / row.entryPrice <= 0.5 ? lastPrice : null;
}

/** True when the leg sits ABOVE the price for this side (long TP, short SL). */
function legIsAbove(side: 'long' | 'short', kind: VestLegKind): boolean {
  return (side === 'long') === (kind === 'tp');
}

/**
 * Why `price` cannot be this leg's trigger, or null when it can.
 *
 * The overlay's rules: Vest refuses a TP / SL at or through the ENTRY
 * (tradovate-service vest/orders.ts assertTpslSide, `invalidTpslPrice`), and a
 * leg through the MARK fills at once (OrderMath.nudgeTp: a flatten by another
 * name). So a long's TP must be above both entry and mark, its SL below both;
 * a short's mirrored. An unknown mark checks the entry only.
 */
export function checkVestLegPrice(
  row: Pick<VestPositionRow, 'side' | 'entryPrice'>,
  kind: VestLegKind,
  price: number,
  mark: number | null,
  decimals = 2,
): string | null {
  const name = kind === 'tp' ? 'TP' : 'SL';
  if (!Number.isFinite(price) || price <= 0) return `${name} needs a positive price`;
  // A price this far from the position's own entry is not this market's (a
  // scale from another chart): never sent.
  if (Math.abs(price - row.entryPrice) / row.entryPrice > VEST_SANE_BAND) {
    return `${name} ${price.toFixed(decimals)} is more than ${VEST_SANE_BAND * 100}% from the entry ${row.entryPrice.toFixed(decimals)} - not sent`;
  }
  const above = legIsAbove(row.side, kind);
  const side = row.side === 'long' ? 'long' : 'short';
  const word = above ? 'above' : 'below';
  const okVs = (ref: number) => (above ? price > ref : price < ref);
  if (!okVs(row.entryPrice)) {
    return `${name} must be ${word} the entry ${row.entryPrice.toFixed(decimals)} on a ${side}`;
  }
  if (mark !== null && !okVs(mark)) {
    return `${name} must be ${word} the mark ${mark.toFixed(decimals)} on a ${side} (it would fill at once)`;
  }
  return null;
}

/** USD P&L of the whole position if it closed at `price` (linear perps). */
export function vestLevelPnl(row: Pick<VestPositionRow, 'side' | 'entryPrice' | 'units'>, price: number): number {
  const dir = row.side === 'long' ? 1 : -1;
  return (price - row.entryPrice) * dir * row.units;
}

function signedText(n: number, decimals: number): string {
  return `${n < 0 ? '-' : '+'}${Math.abs(n).toFixed(decimals)}`;
}

export function usdText(n: number): string {
  return `${n < 0 ? '-' : '+'}$${Math.abs(n).toFixed(2)}`;
}

/** The handle / drag label: price, distance from the entry (market decimals, signed for the position) and USD at that level. */
export function vestLegLabel(
  row: Pick<VestPositionRow, 'side' | 'entryPrice' | 'units'>,
  price: number,
  decimals: number,
): { price: string; distance: string; pnl: string; pnlValue: number } {
  const dir = row.side === 'long' ? 1 : -1;
  const pnlValue = vestLevelPnl(row, price);
  return {
    price: price.toFixed(decimals),
    distance: signedText((price - row.entryPrice) * dir, decimals),
    pnl: usdText(pnlValue),
    pnlValue,
  };
}

/**
 * Where a new (+TP / +SL) line starts: 1% of the reference price (mark, else
 * entry) beyond the further of entry and mark on the leg's side, on the tick,
 * and always on the right side. Not sent until it is dropped.
 */
export function defaultVestDraftPrice(
  row: Pick<VestPositionRow, 'side' | 'entryPrice'>,
  kind: VestLegKind,
  mark: number | null,
  tick: number | null,
): number {
  const ref = mark ?? row.entryPrice;
  const dist = ref * VEST_DRAFT_FRACTION;
  const above = legIsAbove(row.side, kind);
  const base = above ? Math.max(row.entryPrice, ref) : Math.min(row.entryPrice, ref);
  let price = snapVestPrice(above ? base + dist : base - dist, tick);
  const step = tick ?? dist / 10;
  for (let i = 0; i < 1000 && checkVestLegPrice(row, kind, price, mark) !== null; i++) {
    price = snapVestPrice(above ? price + step : price - step, tick);
  }
  return price;
}

/** True when the row's market may receive a command from a chart: a listed wire symbol, never `.VEST`, never an index perp. */
export function vestRowCommandable(row: Pick<VestPositionRow, 'vestSymbol' | 'brokerId'>): boolean {
  if (!row.brokerId) return false;
  const sym = String(row.vestSymbol ?? '').trim().toUpperCase();
  if (!sym || isVestFeedSymbol(sym) || sym.includes('.')) return false;
  return vestChartSymbolFor(sym) !== null;
}

/**
 * Move or create one leg (the overlay's +TP / +SL / leg move). Null when the
 * row may not be commanded from a chart.
 */
export function buildVestSetLegCommand(
  row: Pick<VestPositionRow, 'brokerId' | 'vestSymbol' | 'side' | 'units'> & { accountId?: number | string | null },
  kind: VestLegKind,
  price: number,
  requestId: string,
): VestTpslCommand | null {
  if (!vestRowCommandable(row)) return null;
  if (!Number.isFinite(price) || price <= 0 || !(row.units > 0)) return null;
  const payload: Record<string, unknown> = {
    brokerId: row.brokerId,
    symbol: row.vestSymbol.trim().toUpperCase(),
    action: row.side === 'long' ? 'Sell' : 'Buy',
    newQuantity: row.units,
    [kind === 'tp' ? 'tpPrice' : 'slPrice']: price,
    requestId,
  };
  // The service refuses (ACCOUNT_MISMATCH) when this is not the selected account any more.
  // Its own field: `accountId` keeps the meaning every other client gives it.
  if (row.accountId !== undefined && row.accountId !== null) payload.expectAccountId = row.accountId;
  if (kind === 'sl') payload.slExecution = 'market';
  return { event: VEST_REPLACE_BRACKET, payload };
}

/** Remove one leg by its Vest id. Null without an id or for a row a chart may not command. */
export function buildVestRemoveLegCommand(
  row: Pick<VestPositionRow, 'brokerId' | 'vestSymbol'> & {
    takeProfitOrderId?: string | null; stopLossOrderId?: string | null; accountId?: number | string | null;
  },
  kind: VestLegKind,
  requestId: string,
): VestTpslCommand | null {
  if (!vestRowCommandable(row)) return null;
  const orderId = kind === 'tp' ? row.takeProfitOrderId : row.stopLossOrderId;
  if (typeof orderId !== 'string' || orderId === '') return null;
  const payload: Record<string, unknown> = { brokerId: row.brokerId, orderId, requestId };
  if (row.accountId !== undefined && row.accountId !== null) payload.expectAccountId = row.accountId;
  return { event: VEST_CANCEL_ORDER, payload };
}

// --- replies ------------------------------------------------------------------

export interface VestPendingRequest {
  key: string;
  brokerId: string;
  op: 'set' | 'remove';
  requestId: string;
  /** For a remove: the leg id sent (the ack echoes it). */
  orderId?: string;
  sentAt: number;
}

export type VestReplyOutcome =
  | { kind: 'ok'; message?: string }
  | { kind: 'error'; message: string; code?: string };

function str(v: unknown): string | undefined {
  return typeof v === 'string' && v !== '' ? v : undefined;
}

/**
 * Which pending request a reply answers, and how. Replies are broadcast to
 * every UTT client and every phone (the engine relays them with `io.emit`),
 * and the phone sends no request id, so ONLY an exact match resolves:
 * - `broker:bracket-replaced` / `broker:error`: the echoed `requestId`;
 * - `broker:order-cancelled`: the echoed `requestId`, or the echoed `orderId`
 *   (the leg's Vest UUID the chart sent; the phone cancels by numeric id).
 * Anything else matches nothing and the request's timeout decides.
 */
export function matchVestReply(
  pending: readonly VestPendingRequest[],
  event: string,
  data: unknown,
): { request: VestPendingRequest; outcome: VestReplyOutcome } | null {
  if (!data || typeof data !== 'object') return null;
  const d = data as Record<string, unknown>;
  const brokerId = str(d.brokerId);
  if (!brokerId) return null;
  const onBroker = pending.filter(p => p.brokerId === brokerId);
  if (onBroker.length === 0) return null;
  const requestId = str(d.requestId);
  const message = str(d.message) ?? str(d.error);
  const failed = (fallback: string): VestReplyOutcome =>
    ({ kind: 'error', message: str(d.error) ?? message ?? fallback, code: str(d.code) });

  if (event === VEST_BRACKET_REPLACED) {
    const request = requestId ? onBroker.find(p => p.op === 'set' && p.requestId === requestId) : undefined;
    if (!request) return null;
    if (d.success === false) return { request, outcome: failed('Vest refused the change') };
    return { request, outcome: { kind: 'ok', message: str(d.message) } };
  }
  if (event === VEST_ORDER_CANCELLED) {
    const orderId = d.orderId === undefined || d.orderId === null ? undefined : String(d.orderId);
    const request = onBroker.find(p => p.op === 'remove' && (
      (requestId !== undefined && p.requestId === requestId) || (orderId !== undefined && p.orderId === orderId)
    ));
    if (!request) return null;
    if (d.success === false) return { request, outcome: failed('Vest refused the cancel') };
    return { request, outcome: { kind: 'ok' } };
  }
  if (event === VEST_BROKER_ERROR) {
    const request = requestId ? onBroker.find(p => p.requestId === requestId) : undefined;
    if (!request) return null;
    return { request, outcome: { kind: 'error', message: message ?? 'Vest refused the change', code: str(d.code) } };
  }
  return null;
}
